/** Сезон: шкала и обмен кристаллов — представление без стора, всё приходит пропсами. */
import { Button, Icon, Panel, WindowShell, compactCoins } from "../kit";
import {
  type HeroId,
  SEASON_PASS_STARS,
  type SeasonDef,
  type SeasonReward,
  type SeasonState,
  type ShopItem,
} from "@newfungame/contracts";
import { type SeasonStepView } from "@newfungame/engine";
import { Stars } from "../offers";
import { getLocale } from "../../../i18n";
import { nameKey } from "../format";
import { useT } from "../../../app";
import "./styles.css";

export interface SeasonViewProps {
  season: SeasonState;
  def: SeasonDef;
  daysLeft: number;
  step: number;
  steps: readonly SeasonStepView[];
  unclaimed: SeasonReward;
  /** Цена из состояния игрока: она же будет в счёте. */
  passStars?: number;
  crystals: number;
  skipTickets: number;
  speedTickets: number;
  speedActive: boolean;
  bagCoins: number;
  heroId: HeroId | null;
  canPay: boolean;
  busy: boolean;
  onClaim(): void;
  onBuyPass(): void;
  onTelegram(): void;
  onSpend(item: ShopItem): void;
  onClose(): void;
}

function RewardChips({ r }: { r: SeasonReward }) {
  const locale = getLocale();
  return (
    <span class="win-season__chips">
      <span class="win-task__coins">
        <Icon name="coin" size={16} /> {compactCoins(r.coins, locale)}
      </span>
      {r.crystals > 0 && (
        <span class="win-task__coins">
          <Icon name="crystal" size={16} /> {r.crystals}
        </span>
      )}
      {r.skip > 0 && (
        <span class="win-task__coins">
          <Icon name="clock" size={16} /> {r.skip}
        </span>
      )}
      {r.speed > 0 && (
        <span class="win-task__coins">
          <Icon name="bolt" size={16} /> x2
        </span>
      )}
    </span>
  );
}

/** Сколько шагов шкалы показываем: ближайшие, остальное игрок увидит, когда дойдёт. */
const SEASON_ROWS = 4;

/** Окно сезона: шкала, пропуск, обмен кристаллов. Честно пишем, что пропуск не меняет очки и лигу. */
export function SeasonView(p: SeasonViewProps) {
  const t = useT();
  const { season: s, def } = p;
  const lastStep = p.steps.length;
  const inStep = p.step >= def.steps ? def.pointsPerStep : s.points % def.pointsPerStep;
  const pct = Math.round((inStep / def.pointsPerStep) * 100);
  const first = p.steps.findIndex((x) => !x.reached || !x.freeClaimed || (s.pass && !x.passClaimed));
  const from = first < 0 ? Math.max(0, lastStep - SEASON_ROWS) : first;
  const rows = p.steps.slice(from, from + SEASON_ROWS);
  const can = (price: number) => p.crystals >= price;
  const shop = def.shop;
  return (
    <WindowShell kind="season" title={t("win.season.title", { n: s.number })}>
      <p class="win-tasks__reset" data-testid="season-days">
        <Icon name="clock" size={18} />
        {t("win.season.left", { d: p.daysLeft })}
      </p>
      <Panel class="win-season__progress" testid="season-progress">
        <div class="win-storage__head">
          <span>{t("win.season.step", { n: p.step, max: lastStep })}</span>
          <span data-testid="season-today">{t("win.season.today", { n: s.todayPoints, max: def.dailyCap })}</span>
        </div>
        <span class="win-bar" role="progressbar" aria-valuenow={pct}>
          <span class="win-bar__fill" style={{ width: `${pct}%` }} />
        </span>
      </Panel>
      <ul class="win-season" data-testid="season-steps">
        {rows.map((x) => (
          <li key={x.step} class={`win-season__row${x.reached ? " win-season__row--reached" : ""}`}>
            <span class="win-season__num">{x.step}</span>
            <span class={x.freeClaimed ? "win-season__cell win-season__cell--done" : "win-season__cell"}>
              <RewardChips r={x.free} />
            </span>
            <span
              class={`win-season__cell win-season__cell--pass${x.passClaimed ? " win-season__cell--done" : ""}${s.pass ? "" : " win-season__cell--locked"}`}
            >
              <RewardChips r={x.pass} />
            </span>
          </li>
        ))}
      </ul>
      <div class="win-actions">
        {p.unclaimed.coins > 0 && (
          <Button tone="green" onClick={p.onClaim} data-testid="season-claim">
            {t("win.season.claim")} <RewardChips r={p.unclaimed} />
          </Button>
        )}
        {!s.pass &&
          (p.canPay ? (
            <Button tone="blue" onClick={() => !p.busy && p.onBuyPass()} data-testid="season-pass">
              {t("win.season.pass")} <Stars n={p.passStars ?? SEASON_PASS_STARS} />
            </Button>
          ) : (
            <Button tone="blue" onClick={p.onTelegram} data-testid="season-telegram">
              {t("win.pay.telegram")}
            </Button>
          ))}
        <p class="win-note" data-testid="season-terms">
          {t("win.season.terms")}
        </p>
      </div>
      <Panel class="win-season__shop" testid="season-shop">
        <div class="win-storage__head">
          <span>{t("win.season.shop")}</span>
          <span class="win-task__coins" data-testid="season-crystals">
            <Icon name="crystal" size={18} /> {p.crystals}
          </span>
        </div>
        <ShopRow
          item="speed"
          label={p.speedActive ? t("win.season.speedOn") : t("win.season.speed", { n: shop.speedHours })}
          tickets={p.speedTickets}
          price={shop.speedCrystals}
          enabled={p.speedTickets > 0 || can(shop.speedCrystals)}
          onSpend={p.onSpend}
        />
        <ShopRow
          item="bag"
          label={t("win.season.bag", { coins: compactCoins(p.bagCoins, getLocale()) })}
          tickets={0}
          price={shop.bagCrystals}
          enabled={can(shop.bagCrystals)}
          onSpend={p.onSpend}
        />
        {p.heroId && (
          <ShopRow
            item="hero"
            label={t("win.season.hero", { name: t(`${nameKey("hero", p.heroId)}.name`) })}
            tickets={0}
            price={shop.heroCrystals}
            enabled={can(shop.heroCrystals)}
            onSpend={p.onSpend}
          />
        )}
        {p.skipTickets > 0 && <p class="win-note">{t("win.season.skipTickets", { n: p.skipTickets })}</p>}
      </Panel>
      <div class="win-actions">
        <Button tone="link" onClick={p.onClose} data-testid="season-close">
          {t("win.tasks.close")}
        </Button>
      </div>
    </WindowShell>
  );
}

function ShopRow(p: {
  item: ShopItem;
  label: string;
  tickets: number;
  price: number;
  enabled: boolean;
  onSpend(item: ShopItem): void;
}) {
  const t = useT();
  return (
    <div class="win-season__item" data-testid={`shop-${p.item}`}>
      <span class="win-task__title">{p.label}</span>
      <Button tone={p.enabled ? "green" : "gray"} onClick={() => p.enabled && p.onSpend(p.item)}>
        {p.tickets > 0 ? (
          t("win.season.useTicket", { n: p.tickets })
        ) : (
          <span class="win-price">
            <Icon name="crystal" size={18} /> {p.price}
          </span>
        )}
      </Button>
    </div>
  );
}
